import {
  normalizeRealtimeTags,
  type RealtimeEvent,
  type RealtimeTag,
  realtimeEventSchema,
} from "@blis/realtime/tags";
import { Sentry } from "@blis/telemetry/client";

import { getApiUrl } from "@/lib/api";

const SENTRY_CATEGORY = "realtime";

export type RealtimeStatus = "connecting" | "open" | "closed";

type InvalidationHandler = () => void;

type RealtimeClientOptions = {
  createEventSource?: (url: string) => EventSource;
  getRealtimeUrl?: () => string;
  getDocumentVisibilityState?: () => DocumentVisibilityState | undefined;
  addVisibilityListener?: (listener: () => void) => void;
  removeVisibilityListener?: (listener: () => void) => void;
};

const EVENT_SOURCE_CONNECTING = 0;
const EVENT_SOURCE_OPEN = 1;
const EVENT_SOURCE_CLOSED = 2;

export class BrowserRealtimeClient {
  private es: EventSource | null = null;
  private recoverTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly reconnectHandlers = new Set<() => void>();
  private readonly statusHandlers = new Set<() => void>();
  private readonly handlersByTag = new Map<
    RealtimeTag,
    Set<InvalidationHandler>
  >();
  private subscriptionCount = 0;
  private status: RealtimeStatus = "closed";
  private hasOpenedOnce = false;
  private notifyReconnectOnNextOpen = false;
  private visibilityListener: (() => void) | null = null;
  private visibilityAttached = false;

  private readonly createEventSource: (url: string) => EventSource;
  private readonly getRealtimeUrl: () => string;
  private readonly getDocumentVisibilityState: () =>
    | DocumentVisibilityState
    | undefined;
  private readonly addVisibilityListener: (listener: () => void) => void;
  private readonly removeVisibilityListener: (listener: () => void) => void;

  private static readonly RECOVER_DELAY_MS = 2_000;

  constructor(options: RealtimeClientOptions = {}) {
    this.createEventSource =
      options.createEventSource ??
      ((url) => new EventSource(url, { withCredentials: true }));
    this.getRealtimeUrl =
      options.getRealtimeUrl ?? (() => getApiUrl("/api/realtime"));
    this.getDocumentVisibilityState =
      options.getDocumentVisibilityState ??
      (() =>
        typeof document !== "undefined" ? document.visibilityState : undefined);
    this.addVisibilityListener =
      options.addVisibilityListener ??
      ((listener) => {
        if (typeof document !== "undefined") {
          document.addEventListener("visibilitychange", listener);
        }
      });
    this.removeVisibilityListener =
      options.removeVisibilityListener ??
      ((listener) => {
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", listener);
        }
      });
  }

  subscribe(
    tags: readonly RealtimeTag[],
    handler: InvalidationHandler,
  ): () => void {
    const normalizedTags = normalizeRealtimeTags(tags);
    if (normalizedTags.length === 0) {
      return () => {};
    }

    for (const tag of normalizedTags) {
      let handlers = this.handlersByTag.get(tag);
      if (!handlers) {
        handlers = new Set();
        this.handlersByTag.set(tag, handlers);
      }
      handlers.add(handler);
    }

    this.subscriptionCount++;
    this.breadcrumb("subscribe", {
      activeSubscriptions: this.subscriptionCount,
      tags: normalizedTags,
    });
    this.attachVisibilityListenerIfNeeded();
    this.openIfNeeded();

    let released = false;
    return () => {
      if (released) return;
      released = true;

      for (const tag of normalizedTags) {
        const handlers = this.handlersByTag.get(tag);
        if (!handlers) continue;
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.handlersByTag.delete(tag);
        }
      }

      this.subscriptionCount--;
      this.breadcrumb("unsubscribe", {
        activeSubscriptions: this.subscriptionCount,
        tags: normalizedTags,
      });

      if (this.subscriptionCount === 0) {
        this.detachVisibilityListenerIfNeeded();
        this.closeStream();
      }
    };
  }

  subscribeReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler);
    return () => {
      this.reconnectHandlers.delete(handler);
    };
  }

  subscribeStatus(handler: () => void): () => void {
    this.statusHandlers.add(handler);
    return () => {
      this.statusHandlers.delete(handler);
    };
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  dispose(): void {
    if (this.recoverTimer) {
      clearTimeout(this.recoverTimer);
      this.recoverTimer = null;
    }
    this.detachVisibilityListenerIfNeeded(true);
    this.es?.close();
    this.es = null;
    this.handlersByTag.clear();
    this.reconnectHandlers.clear();
    this.statusHandlers.clear();
    this.subscriptionCount = 0;
    this.status = "closed";
    this.hasOpenedOnce = false;
    this.notifyReconnectOnNextOpen = false;
  }

  private openIfNeeded(): void {
    if (this.subscriptionCount === 0) return;
    if (this.es && this.es.readyState !== EVENT_SOURCE_CLOSED) return;

    const url = this.getRealtimeUrl();
    log("opening", { url });
    this.breadcrumb("opening");

    const es = this.createEventSource(url);
    this.es = es;
    this.setStatus("connecting");

    es.addEventListener("open", () => {
      if (this.es !== es) return;
      const shouldNotifyReconnect = this.notifyReconnectOnNextOpen;
      this.hasOpenedOnce = true;
      this.notifyReconnectOnNextOpen = false;
      log("open", { reconnect: shouldNotifyReconnect });
      this.setStatus("open");
      if (!shouldNotifyReconnect) return;
      for (const fn of this.reconnectHandlers) {
        try {
          fn();
        } catch {
          // One reconnect handler shouldn't break the others.
        }
      }
    });

    es.addEventListener("change", (rawEvent) => {
      const messageEvent = rawEvent as MessageEvent<string>;
      let parsed: unknown;
      try {
        parsed = JSON.parse(messageEvent.data);
      } catch (err) {
        log("dropped non-JSON event", { err });
        this.breadcrumb("dropped non-JSON event", undefined, "warning");
        return;
      }

      const result = realtimeEventSchema.safeParse(parsed);
      if (!result.success) {
        log("dropped invalid event", { issues: result.error.issues });
        this.breadcrumb("dropped invalid event", undefined, "warning");
        return;
      }

      const event: RealtimeEvent = {
        ...result.data,
        tags: normalizeRealtimeTags(result.data.tags),
      };
      log("event", event);
      this.dispatch(event);
    });

    es.addEventListener("error", () => {
      const state = es.readyState;
      log("error", { readyState: state });
      this.breadcrumb("error", { readyState: state }, "warning");
      if (this.es !== es) return;

      this.setStatus(state === EVENT_SOURCE_CLOSED ? "closed" : "connecting");
      if (state === EVENT_SOURCE_CLOSED) {
        this.notifyReconnectOnNextOpen ||= this.hasOpenedOnce;
        this.scheduleRecover();
      }
    });
  }

  private dispatch(event: RealtimeEvent): void {
    const handlers = new Set<InvalidationHandler>();
    for (const tag of event.tags) {
      const tagHandlers = this.handlersByTag.get(tag);
      if (!tagHandlers) continue;
      for (const handler of tagHandlers) {
        handlers.add(handler);
      }
    }

    for (const handler of handlers) {
      try {
        handler();
      } catch {
        // One subscriber shouldn't break the others.
      }
    }
  }

  private scheduleRecover(): void {
    if (this.recoverTimer) return;
    this.recoverTimer = setTimeout(() => {
      this.recoverTimer = null;
      if (this.subscriptionCount === 0) return;
      if (this.es && this.es.readyState === EVENT_SOURCE_OPEN) return;
      log("recovering from CLOSED state");
      this.forceReopen(this.hasOpenedOnce);
    }, BrowserRealtimeClient.RECOVER_DELAY_MS);
  }

  private forceReopen(notifyReconnect: boolean): void {
    if (this.recoverTimer) {
      clearTimeout(this.recoverTimer);
      this.recoverTimer = null;
    }
    this.notifyReconnectOnNextOpen ||= notifyReconnect;
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.openIfNeeded();
  }

  private closeStream(): void {
    if (this.recoverTimer) {
      clearTimeout(this.recoverTimer);
      this.recoverTimer = null;
    }
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.notifyReconnectOnNextOpen = false;
    this.setStatus("closed");
  }

  private attachVisibilityListenerIfNeeded(): void {
    if (this.visibilityAttached) return;
    this.visibilityListener = () => {
      if (this.getDocumentVisibilityState() !== "visible") return;
      if (this.subscriptionCount === 0) return;
      if (this.es && this.es.readyState === EVENT_SOURCE_OPEN) return;
      log("visible while disconnected — forcing reopen");
      this.forceReopen(this.hasOpenedOnce);
    };
    this.addVisibilityListener(this.visibilityListener);
    this.visibilityAttached = true;
  }

  private detachVisibilityListenerIfNeeded(force = false): void {
    if (!this.visibilityAttached) return;
    if (!force && this.subscriptionCount > 0) return;
    if (this.visibilityListener) {
      this.removeVisibilityListener(this.visibilityListener);
    }
    this.visibilityListener = null;
    this.visibilityAttached = false;
  }

  private setStatus(next: RealtimeStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.breadcrumb(`status:${next}`);
    for (const handler of this.statusHandlers) {
      try {
        handler();
      } catch {
        // One status listener shouldn't break the others.
      }
    }
  }

  private breadcrumb(
    message: string,
    data?: Record<string, unknown>,
    level: "info" | "warning" | "error" = "info",
  ): void {
    try {
      Sentry.addBreadcrumb({
        category: SENTRY_CATEGORY,
        type: "info",
        level,
        message,
        data,
      });
    } catch {
      // Sentry may not be initialized; diagnostics must not block the path.
    }
  }
}

let singleton: BrowserRealtimeClient | null = null;

export function getRealtimeClient(): BrowserRealtimeClient {
  if (!singleton) {
    singleton = new BrowserRealtimeClient();
  }
  return singleton;
}

export function _setRealtimeClient(client: BrowserRealtimeClient | null): void {
  singleton = client;
}

function log(message: string, data?: unknown): void {
  if (!isLoggingEnabled()) return;
  if (data === undefined) {
    // biome-ignore lint/suspicious/noConsole: dev/opt-in realtime diagnostics
    console.debug(`[realtime] ${message}`);
  } else {
    // biome-ignore lint/suspicious/noConsole: dev/opt-in realtime diagnostics
    console.debug(`[realtime] ${message}`, data);
  }
}

function isLoggingEnabled(): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem("realtimeDebug") === "1";
    }
  } catch {
    // Ignore — storage may be unavailable in some contexts.
  }
  return false;
}
